import {ChangeDetectionStrategy, Component, DestroyRef, ElementRef, EventEmitter, inject, Input, Output, ViewChild} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {MatAutocomplete, MatAutocompleteModule} from '@angular/material/autocomplete';
import {MxSelector} from '@aviary/store/store.types';
import {alwaysFalse} from '@aviary/store/common/common.selectors';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ControlRef, InputMode} from '@aviary/forms/fields/form-fields.types';
import {hasNoValue, hasValue, isDefined} from '@aviary/store/common/typing.helpers';
import {BaseFormFieldComponent} from '@aviary/forms/fields/base-form-field/base-form-field.component';
import {MatIcon} from '@angular/material/icon';
import {MatInput} from '@angular/material/input';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {SelectPipe} from '@aviary/pipes/select.pipe';
import {getExtendedControlFrom} from '@aviary/forms/form.helpers';
import {ExtendedFormGroup} from '@aviary/forms/extensions/extended-form-group/extended-form-group';
import {ExtendedFormControl} from '@aviary/forms/extensions/extended-form-control/extended-form-control';
import {provideControlRefTokenFor} from '@aviary/providers/control-ref.provider';
import {IconButtonComponent} from '@aviary/buttons/icon-button/icon-button.component';
import {ErrorStateMatcher} from '@angular/material/core';

@Component({
  selector: 'mx-input-form-field',
  templateUrl: './input-form-field.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideControlRefTokenFor(InputFormFieldComponent)],
  imports: [BaseFormFieldComponent, MatIcon, MatInput, ReactiveFormsModule, MatAutocompleteModule, MatProgressSpinner, SelectPipe, IconButtonComponent]
})
export class InputFormFieldComponent implements ControlRef {

  @ViewChild('inputElement', {static: true}) private inputElement: ElementRef<HTMLInputElement>;

  @Input() public inputType = 'text';
  @Input() public overrideLabel?: string;
  @Input() public hintLabel = '';
  @Input() public externalLabel = false;
  @Input() public uppercaseExternalLabel = true;
  @Input() public placeholder = '';
  @Input() public includeSearchIcon = false;
  @Input() public includeDollarSign = false;
  @Input() public postfixText = '';
  @Input() public alignRight = false;
  @Input() public overrideBrowserAutocomplete?: string; // To be used for forcibly "tricking" browsers & pw managers to stop autocompleting the input
  @Input() public maxLength?: number;
  @Input() public inputMode?: InputMode;

  @Input()
  public set includeClearButton(value: boolean) {
    this._includeClearButton = value;

    this.updateClearButtonFor(this.control?.value ?? '');
  }

  @Input() public replaceClearButtonWithSpinner: MxSelector<boolean> = alwaysFalse;  // Ignored when `showClearButton` is `false`.
  @Input() public auxiliaryButtonIconClass = '';
  @Input() public auxiliaryButtonTooltip: string = '';
  @Input() public autocompleteElement: MatAutocomplete;
  @Input() public forceUppercase: boolean = false;
  @Input() public showValidationErrors: boolean = true;

  @Input()
  public get auxiliaryButtonIconName(): string {
    return this._auxiliaryButtonIconName;
  }

  public set auxiliaryButtonIconName(auxiliaryButtonIconName: string) {
    this._auxiliaryButtonIconName = auxiliaryButtonIconName;
    this.showAuxiliaryButton = !!auxiliaryButtonIconName;
  }

  @Input({required: true})
  public set fieldName(fieldName: string) {
    this._fieldName = fieldName;
    this.initializeControl();
  }

  public get fieldName(): string {
    return this._fieldName;
  }

  @Input({required: true})
  public set formGroup(formGroup: ExtendedFormGroup) {
    this._formGroup = formGroup;
    this.initializeControl();
  }

  public get formGroup(): ExtendedFormGroup {
    return this._formGroup;
  }

  @Input({required: false})
  public set enabled(enabled: boolean) {
    this._enabled = enabled;
    if (isDefined(this.control) && !enabled) this.control.disable();
  }

  public get enabled(): boolean {
    return this._enabled;
  }

  @Output() public readonly keydownEnter: EventEmitter<never> = new EventEmitter<never>();
  @Output() public readonly auxiliaryButtonClick: EventEmitter<never> = new EventEmitter<never>();
  @Output() public readonly inputFocused: EventEmitter<never> = new EventEmitter<never>();
  @Output() public readonly inputBlurred: EventEmitter<never> = new EventEmitter<never>();

  public control: ExtendedFormControl;  // It's tempting to rename this to "formControl", but that's a reserved name and causes confusing errors.  So don't.
  public showClearButton = false;
  public showAuxiliaryButton = false;
  public focused = false;

  public get validationErrorStateMatcher(): ErrorStateMatcher {
    return this.showValidationErrors ? this.defaultErrorStateMatcher : this.hiddenValidationErrorStateMatcher;
  }

  private _auxiliaryButtonIconName: string;
  private _includeClearButton: boolean = true;
  private _fieldName: string;
  private _formGroup: ExtendedFormGroup;
  private _enabled = true;
  private readonly defaultErrorStateMatcher: ErrorStateMatcher = inject(ErrorStateMatcher);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly hiddenValidationErrorStateMatcher: ErrorStateMatcher = {isErrorState: (): boolean => false};

  public initializeControl(): void {
    if (hasNoValue(this._formGroup) || hasNoValue(this._fieldName)) return;

    this.control = getExtendedControlFrom(this._formGroup, this._fieldName);
    if (hasNoValue(this.control)) return;

    if (!this._enabled) this.control.disable();

    this.updateClearButtonFor(this.control.value);

    this.control.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => this.updateClearButtonFor(value));
  }

  public onFocusInput(): void {
    this.focused = true;
    this.inputFocused.emit();
  }

  public onBlurInput(): void {
    this.focused = false;

    this.inputBlurred.emit();
    if (this.forceUppercase) this.control.patchValue(this.control.value.toUpperCase());
  }

  public onKeydownEnter(): void {
    this.keydownEnter.emit();
  }

  public onClickClearButton(): void {
    this.control.patchValueAndMarkAsDirty('');
  }

  public onClickAuxiliaryButton(): void {
    this.auxiliaryButtonClick.emit();
  }

  public moveCursorToEnd(): void {
    const endPosition: number = this.inputElement.nativeElement.value.length;
    this.inputElement.nativeElement.setSelectionRange(endPosition, endPosition);
  }

  private updateClearButtonFor(value: unknown): void {
    this.showClearButton = this._includeClearButton && hasValue(value) && String(value).length > 0;
  }
}
